import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import './MyFeedback.css';

const MyFeedback = () => {
  const navigate = useNavigate();
  const [feedback, setFeedback] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  useEffect(() => {
    fetchFeedback();
  }, [filter, page]);

  const fetchFeedback = async () => {
    try {
      const token = localStorage.getItem('accessToken');
      const apiUrl = process.env.REACT_APP_API_URL || '';
      const response = await fetch(
        `${apiUrl}/api/feedback/my-feedback?status=${filter}&page=${page}&limit=10`,
        {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        }
      );

      const data = await response.json();
      if (response.ok) {
        setFeedback(data.feedback);
        setTotalPages(data.totalPages);
      }
    } catch (err) {
      console.error('Error fetching feedback:', err);
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = (status) => {
    const badges = {
      pending: { label: 'Pending', class: 'status-pending', icon: '⏳' },
      in_progress: { label: 'In Progress', class: 'status-in-progress', icon: '🔄' },
      resolved: { label: 'Resolved', class: 'status-resolved', icon: '✅' },
      closed: { label: 'Closed', class: 'status-closed', icon: '🔒' }
    };
    const badge = badges[status] || badges.pending;
    return (
      <span className={`status-badge ${badge.class}`}>
        <span className="badge-icon">{badge.icon}</span>
        {badge.label}
      </span>
    );
  };

  const getTypeLabel = (type) => {
    const types = {
      question: { label: 'Question', icon: '❓' },
      feature_request: { label: 'Feature Request', icon: '💡' },
      bug_report: { label: 'Bug Report', icon: '🐛' },
      country_request: { label: 'Country Request', icon: '🌍' },
      general: { label: 'General', icon: '💬' }
    };
    const typeInfo = types[type] || types.general;
    return (
      <span className="type-label">
        <span className="type-icon">{typeInfo.icon}</span>
        {typeInfo.label}
      </span>
    );
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  if (loading) {
    return <div className="loading">Loading your feedback...</div>;
  }

  return (
    <div className="my-feedback-container">
      <div className="feedback-header">
        <h1>My Feedback</h1>
        <button onClick={() => navigate('/contact-us')} className="btn-new-feedback">
          + New Feedback
        </button>
      </div>

      <div className="feedback-filters">
        <button
          className={filter === 'all' ? 'active' : ''}
          onClick={() => { setFilter('all'); setPage(1); }}
        >
          All
        </button>
        <button
          className={filter === 'pending' ? 'active' : ''}
          onClick={() => { setFilter('pending'); setPage(1); }}
        >
          Pending
        </button>
        <button
          className={filter === 'in_progress' ? 'active' : ''}
          onClick={() => { setFilter('in_progress'); setPage(1); }}
        >
          In Progress
        </button>
        <button
          className={filter === 'resolved' ? 'active' : ''}
          onClick={() => { setFilter('resolved'); setPage(1); }}
        >
          Resolved
        </button>
      </div>

      {feedback.length === 0 ? (
        <div className="no-feedback">
          <p>No feedback found.</p>
          <button onClick={() => navigate('/contact-us')} className="btn-primary">
            Submit Your First Feedback
          </button>
        </div>
      ) : (
        <>
          <div className="feedback-list">
            {feedback.map(item => (
              <div key={item.feedback_id} className="feedback-item">
                <div className="feedback-item-header">
                  <div className="feedback-meta">
                    {getTypeLabel(item.type)}
                    {getStatusBadge(item.status)}
                  </div>
                  <div className="feedback-date">{formatDate(item.created_at)}</div>
                </div>

                <h3 className="feedback-subject">{item.subject}</h3>
                <p className="feedback-message">{item.message}</p>

                {item.admin_response && (
                  <div className="admin-response">
                    <div className="response-header">
                      <strong>Admin Response:</strong>
                      {item.responder_first_name && (
                        <span className="responder"> by {item.responder_first_name}</span>
                      )}
                      {item.responded_at && (
                        <span className="response-date"> • {formatDate(item.responded_at)}</span>
                      )}
                    </div>
                    <p className="response-text">{item.admin_response}</p>
                  </div>
                )}
              </div>
            ))}
          </div>

          {totalPages > 1 && (
            <div className="pagination">
              <button
                onClick={() => setPage(p => Math.max(1, p - 1))}
                disabled={page === 1}
              >
                Previous
              </button>
              <span>Page {page} of {totalPages}</span>
              <button
                onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                disabled={page === totalPages}
              >
                Next
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default MyFeedback;
